import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Skeleton,
  useTheme
} from '@mui/material'
import {
  TrendingUp as TrendingIcon,
  Description as ReportIcon,
  CheckCircle as FinalIcon,
  Edit as DraftIcon
} from '@mui/icons-material'
import { PatientWithHistory, TimelineEntry } from '../../../types/api'

interface QuickStatsPanelProps {
  patient: PatientWithHistory | null
  reports: any[]
  loadingReports?: boolean
  onViewReport?: (report: { id: string; status: 'draft' | 'final' }) => void
}

// Grupează timeline-ul pe anatomie
const groupTimelineByAnatomy = (timeline: TimelineEntry[]) => {
  const groups: { [key: string]: TimelineEntry[] } = {}
  
  const studiesOnly = timeline.filter(entry => entry.type === 'study')
  
  studiesOnly.forEach(entry => {
    const anatomy = entry.anatomy_group || 'ALTELE'
    if (!groups[anatomy]) {
      groups[anatomy] = []
    }
    groups[anatomy].push(entry)
  })
  
  return groups
}

/**
 * QuickStatsPanel - Panel lateral cu statistici și rapoarte
 * 
 * Secțiuni:
 * 1. Distribuție Anatomică (bar chart simplu)
 * 2. Tracking Activ (pentru follow-up)
 * 3. Rapoarte Recente (listă click-abilă)
 */
const QuickStatsPanel: React.FC<QuickStatsPanelProps> = ({
  patient,
  reports,
  loadingReports,
  onViewReport
}) => {
  const theme = useTheme()
  const { designSystem } = theme
  const colors = theme.palette.mode === 'dark' 
    ? designSystem.colors.dark 
    : designSystem.colors.light

  if (!patient) {
    return (
      <Box>
        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={150} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} />
      </Box>
    )
  }

  const anatomyGroups = groupTimelineByAnatomy(patient.timeline)
  const totalStudies = patient.studies_count || 0

  // Calculează tracking data
  const trackingData = Object.entries(anatomyGroups)
    .filter(([_, entries]) => entries.length >= 2)
    .map(([anatomy, entries]) => {
      const sortedEntries = entries.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      const latestStudy = sortedEntries[0]
      const previousStudy = sortedEntries[1]
      
      const daysDiff = Math.abs(
        (new Date(latestStudy.date).getTime() - new Date(previousStudy.date).getTime()) 
        / (1000 * 60 * 60 * 24)
      )
      
      return {
        anatomy,
        studies: entries.length,
        interval: Math.round(daysDiff),
        trend: entries.length >= 3 ? 'URMĂRIRE ACTIVĂ' : 'CONTROL',
      }
    })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Distribuție Anatomică */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.default}`,
          borderRadius: `${designSystem.components.card.borderRadius}px`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated 
        }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Distribuție Anatomică
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {Object.entries(anatomyGroups).length === 0 ? (
            <Typography variant="body2" sx={{ color: colors.text.secondary, fontStyle: 'italic' }}>
              Nu există date
            </Typography>
          ) : (
            Object.entries(anatomyGroups)
              .sort((a, b) => b[1].length - a[1].length)
              .slice(0, 5) // Max 5 entries
              .map(([anatomy, entries]) => {
                const percentage = totalStudies > 0 ? (entries.length / totalStudies) * 100 : 0
                return (
                  <Box key={anatomy} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.primary,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                        }}
                      >
                        {anatomy}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.accent.primary,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                        }}
                      >
                        {entries.length}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: `${colors.accent.primary}20`,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          backgroundColor: colors.accent.primary,
                        }
                      }}
                    />
                  </Box>
                )
              })
          )}
        </Box>
      </Paper>

      {/* Tracking Activ */}
      {trackingData.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.border.default}`,
            borderRadius: `${designSystem.components.card.borderRadius}px`,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ 
            p: 2, 
            borderBottom: `1px solid ${colors.border.default}`,
            backgroundColor: colors.background.elevated 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingIcon sx={{ fontSize: 16, color: designSystem.colors.status.warning }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: colors.text.primary,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Tracking Activ
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            {trackingData.map((track, index) => (
              <Box
                key={track.anatomy}
                sx={{
                  p: 1.5,
                  mb: index < trackingData.length - 1 ? 1 : 0,
                  backgroundColor: colors.background.elevated,
                  borderRadius: `${designSystem.components.card.borderRadius}px`,
                  border: `1px solid ${colors.border.default}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.85rem',
                    mb: 0.5,
                  }}
                >
                  {track.anatomy}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {track.studies} studii • Interval: {track.interval} zile
                </Typography>
                <Chip
                  label={track.trend}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    backgroundColor: track.trend === 'URMĂRIRE ACTIVĂ' 
                      ? `${designSystem.colors.status.warning}20`
                      : `${designSystem.colors.status.info}20`,
                    color: track.trend === 'URMĂRIRE ACTIVĂ'
                      ? designSystem.colors.status.warning
                      : designSystem.colors.status.info,
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Rapoarte Recente */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.default}`,
          borderRadius: `${designSystem.components.card.borderRadius}px`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportIcon sx={{ fontSize: 16, color: designSystem.colors.status.success }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Rapoarte ({reports.length})
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 1.5, maxHeight: 300, overflowY: 'auto' }}>
          {loadingReports ? (
            <>
              <Skeleton variant="rounded" height={50} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={50} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={50} />
            </>
          ) : reports.length === 0 ? (
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.text.secondary, 
                fontStyle: 'italic',
                textAlign: 'center',
                py: 2 
              }}
            >
              Nu există rapoarte disponibile
            </Typography>
          ) : (
            reports
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .slice(0, 10) // Max 10 reports
              .map((report) => (
                <Box
                  key={report.id}
                  onClick={() => onViewReport?.({
                    id: report.id,
                    status: report.status as 'draft' | 'final'
                  })}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: `${designSystem.components.card.borderRadius}px`,
                    border: `1px solid ${colors.border.default}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: colors.background.elevated,
                      borderColor: colors.accent.primary,
                    },
                    '&:last-child': { mb: 0 }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: colors.text.primary,
                          fontSize: '0.8rem',
                          mb: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {report.study_description || 'Raport medical'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontSize: '0.7rem',
                        }}
                      >
                        {new Date(report.created_at).toLocaleDateString('ro-RO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Box>
                    <Chip
                      icon={report.status === 'final' ? <FinalIcon /> : <DraftIcon />}
                      label={report.status === 'final' ? 'Final' : 'Draft'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        ml: 1,
                        backgroundColor: report.status === 'final'
                          ? `${designSystem.colors.status.success}20`
                          : `${designSystem.colors.status.warning}20`,
                        color: report.status === 'final'
                          ? designSystem.colors.status.success
                          : designSystem.colors.status.warning,
                        '& .MuiChip-icon': {
                          fontSize: 14,
                          color: 'inherit',
                        },
                        '& .MuiChip-label': { px: 0.75 }
                      }}
                    />
                  </Box>
                </Box>
              ))
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default QuickStatsPanel

